// @flow
import React from 'react'
import { withRouter } from 'react-router-dom'
import { parse, stringify } from 'query-string'
import Tree, { TreeNode } from 'er2-rc-tree'
import * as routes from '../constants/routes'
import stylesFicus from '../styles'
import type { CollaborationType } from '../reducers/collaboration'
import type { CollaborationSettingsType } from '../reducers/collaboration_settings'
import type { ExceptionType, SelectFrameworkBarType } from '../reducers/select_framework_bar'
import type { SettingsBarType } from '../reducers/settings'

type Props = {
    addBookmark: Function,
    collaboration: CollaborationType,
    collaborationSettings: CollaborationSettingsType,
    frameworks: FrameworkType[],
    fetchFrameworks: Function,
    ficus: SelectFrameworkBarType,
    frameworkException: ExceptionType,
    getFicusInfo: Function,
    handledSetFramework: Function,
    location: { pathname: string, hash: string, search: {} },
    onLoadFicusNode: Function,
    setConfig: Function,
    setFicusData: Function,
    setFicusInfoUrl: Function,
    settings: SettingsBarType,
    theme: stylesFicus,
    history: Function,
}

type State = {
    autoExpandParent: boolean,
    expandedKeys: string[],
    filterEmpty: boolean,
    loadingFicusNode?: string,
    searchText: string,
    selectedKeys: string[],
    parentKeys: {},
    reloadingTimer: number,
}

/**
 * The frameworks tree
 */
class Frameworks extends React.Component<Props, State> {
    dragState: any

    constructor(props: Props) {
        super(props)
        this.state = {
            searchText: '',
            autoExpandParent: true,
            filterEmpty: false,
            expandedKeys: [],
            loadFicusNode: null,
            selectedKeys: [],
            parentKeys: {},
            reloadingTimer: 0,
        }

        this.dragState = {
            isDragging: false,
        }
    }

    componentDidMount() {
        this.updateFrameworkData(this.props)
    }

    componentWillReceiveProps(newProps: Props) {
        this.updateFrameworkData(newProps)
        // Check for gateway timeout
        if (newProps.frameworkException && newProps.frameworkException.status === 504) {
            if (!this.state.reloadingTimer) {
                const timer = setTimeout(() => {
                    console.error('No frameworks found, retrying...')
                    this.setState({ ...this.state, reloadingTimer: 0 })
                    newProps.fetchFrameworks()
                }, 5000)
                this.setState({ ...this.state, reloadingTimer: timer })
            }
        }
    }

    updateFrameworkData(newProps: Props) {
        if (newProps.frameworks) {
            this.buildKeysToParents(newProps)
            this.checkForPreselectedFramework(newProps)
        }
    }

    /**
     * rc-tree requires that the expanded property is set on every parent node whose child we want
     *   to display. Used by addParentOfExpandedKeys
     */
    buildKeysToParents(newProps: Props) {
        const parentKeys: { [key: string]: string, } = {}
        const searchLoop = (data) => {
            data.forEach((item) => {
                if (item.children) {
                    item.children.forEach((c) => {
                        parentKeys[c.data.path] = item.data.path
                    })
                    searchLoop(item.children)
                }
            })
        }
        searchLoop(newProps.frameworks)
        this.setState({ ...this.state, parentKeys })
    }

    addParentsOfExpandedKeys(expandedKeys: string[]) {
        expandedKeys.forEach((k) => {
            let parentKey = k
            while (parentKey) {
                if (expandedKeys.indexOf(parentKey) < 0) {
                    expandedKeys.push(parentKey)
                }
                parentKey = this.state.parentKeys[parentKey]
            }
        })
        return expandedKeys
    }

    checkForPreselectedFramework(props) {
        // If a framework is already selected, then don't override.
        const preselected = props.ficus.ficusNode
        if (preselected) {
            const expandedKeysWithParents = this.addParentsOfExpandedKeys([preselected])
            const mergedKeys = new Set([...this.state.expandedKeys, ...expandedKeysWithParents])
            this.setState({ ...this.state, expandedKeys: [...mergedKeys], selectedKeys: [preselected] })
        }
    }

    onLoadData = (ficusNode) => {
        return new Promise((resolve) => {
            this.props.fetchFrameworks({
                nodePath: ficusNode.props.data.path,
                fnFinished: () => {
                    const preselected = ficusNode.props.data.path
                    const expandedKeysWithParents = this.addParentsOfExpandedKeys([preselected])
                    const mergedKeys = new Set([...this.state.expandedKeys, ...expandedKeysWithParents])
                    this.setState({ ...this.state, expandedKeys: [...mergedKeys], selectedKeys: [preselected] })
                    resolve()
                },
            })
        })
    }

    selectFramework(ficusNode: string) {
        this.props.setFicusData(ficusNode)
        this.updateHistory(ficusNode)
    }

    updateHistory(ficusNode) {
        this.props.addBookmark('bookmarksMRU', ficusNode)
        const params = { ...parse(this.props.location.search), theme: ficusNode }
        this.props.history.push({ search: stringify(params) })
    }

    render() {
        const theme = this.props.theme
        const getMatcher = () => (this.state.searchText ? new RegExp(this.state.searchText, 'i') : false)
        const onReloadFrameworks = () => this.props.fetchFrameworks()
        const onSearch = () => {
            const matcher = getMatcher()

            // Calculate the expanded nodes
            const expandedKeys = []
            const matchLoop = (data) => {
                data.forEach((item) => {
                    if (matcher && matcher.test(item.text)) {
                        expandedKeys.push(item.data.path)
                    }
                    if (item.children) {
                        matchLoop(item.children)
                    }
                })
            }
            matchLoop(this.props.frameworks)

            const expandedKeysWithParents = this.addParentsOfExpandedKeys(expandedKeys)
            this.setState({ expandedKeys: expandedKeysWithParents, autoExpandParent: true })
        }
        const onSelect = (nodeList, e) => {
            const data = e.node.props.data
            if (nodeList.length) {
                if (data.has_temporal) {
                    this.selectFramework(nodeList[0])
                } else if (data.has_rasters) {
                    this.selectFramework(nodeList[0])
                } else if (data.has_vectors) {
                    this.selectFramework(nodeList[0])
                }
                this.setState({ loadingFicusNode: nodeList[0] })
            }
        }
        const onExpand = (expandedKeys) => {
            this.setState({ ...this.state, autoExpandParent: false, expandedKeys })
        }
        const onFilterEmpty = () => this.setState({ ...this.state, filterEmpty: !this.state.filterEmpty })

        const titleEntryStyle = {
            // backgroundColor: `rgba(255,255,255,${this.props.settings.popupOpacity})`,
        }
        const buildTreeData = (data) => {
            const matcher = getMatcher()
            return data.map((item) => {
                const classList = [theme.treeLeafEntry]
                let hyperlink
                let loading

                if (matcher && matcher.test(item.text)) classList.push(this.props.theme.search)
                if (item.data.error) classList.push(this.props.theme.metaError)

                if (item.data.has_rasters) classList.push(this.props.theme.hasRasters)
                else if (item.data.has_temporal) classList.push(this.props.theme.hasTemporal)
                else if (item.data.has_vectors) classList.push(this.props.theme.hasTransim)
                else classList.push(this.props.theme.initial)

                if (this.props.ficus.fetchingFicusData && item.data.path === this.state.loadingFicusNode) {
                    loading = (
                        <img
                            alt="loading frameworks"
                            style={{ marginLeft: '4px' }}
                            src="/static/er2_ficus/img/loading.gif"
                        />
                    )
                }

                if (item.data.description_path) {
                    const linkStyle = { marginLeft: '4px' }
                    const gotoInfo = (e) => {
                        // Load instructions
                        this.props.getFicusInfo(item.data.path)

                        // Don't select this node; just change the route and keep the old hash
                        // this.props.history.push(`${routes.INSTRUCTIONS_FRAMEWORK}${this.props.location.hash}`)
                        const params = { ...parse(this.props.location.search), infoNode: item.data.path }
                        // this.props.history.push({ search: stringify(params), pathname: routes.INSTRUCTIONS_FRAMEWORK })
                        // Use this to open in a new tab
                        const url = `${routes.INSTRUCTIONS_FRAMEWORK}?token=${params.token}&infoNode=${params.infoNode}`
                        window.open(url, params.infoNode)
                        e.stopPropagation()
                    }

                    hyperlink = (
                        <span style={linkStyle}>
                            <a onClick={gotoInfo} target="node" role="button" tabIndex={-1} style={{ marginLeft: '10px' }}>
                                <i className={'fa fa-info'} />
                            </a>
                        </span>
                    )
                }
                if (item.children.length) {
                    if (!this.state.filterEmpty || !item.data.is_empty) {
                        return (
                            <TreeNode
                                key={item.data.path}
                                data={{ ...item.data, text: item.text }}
                                title={(<span className={theme.treeFrameworkTitleContainer} style={titleEntryStyle}><span>{item.text}</span>{hyperlink}{loading}</span>)}
                                className={classList.join(' ')}
                            >
                                {buildTreeData(item.children)}
                            </TreeNode>
                        )
                    }
                } else if (!this.state.filterEmpty || item.data.has_rasters) {
                    return (<TreeNode
                        key={item.data.path}
                        data={item.data}
                        title={(<span className={theme.treeFrameworkTitle} style={titleEntryStyle}>{item.text}{hyperlink}{loading}</span>)}
                        className={classList.join(' ')}
                    />)
                }
                return (null)
            })
        }

        const treeNodes = buildTreeData(this.props.frameworks || []).filter(d => d !== null)
        // TODO: fix estimation to use actual height of elements.
        const treeHeight = document.documentElement ? document.documentElement.clientHeight - 200 : 0

        let frameworkDiv
        const frameworksDivStyle = { position: 'absolute', top: '0' }
        if (this.props.frameworkException) {
            frameworkDiv = (<em style={{ textAlign: 'center' }}>
                <h4>Sorry, got an error retrieving themes:<br />
                    {`${this.props.frameworkException.status} ${this.props.frameworkException.statusText}`}
                </h4></em>)
            frameworksDivStyle.width = '100%'
        } else if (this.props.frameworks === undefined) {
            frameworkDiv = (<em style={{ textAlign: 'center' }}><h4>Frameworks are undefined</h4></em>)
            frameworksDivStyle.width = '100%'
        } else if (this.props.frameworks === null) {
            frameworkDiv = (
                <div style={{ textAlign: 'center', marginTop: '30px' }}>
                    <img
                        alt="loading frameworks"
                        style={{ width: '25%' }}
                        src="/static/er2_ficus/img/dualring.gif"
                    />
                </div>
            )
        } else if (this.props.frameworks.length === 0) {
            frameworkDiv = (<em style={{ textAlign: 'center' }}><h4>Sorry, no themes are available</h4></em>)
            frameworksDivStyle.width = '100%'
        } else {
            frameworkDiv = (
                <div className={theme.container}>
                    <Tree
                        autoExpandParent={this.state.autoExpandParent}
                        draggable
                        expandedKeys={this.state.expandedKeys}
                        loadData={this.onLoadData}
                        onSelect={(...args) => onSelect(...args)}
                        onExpand={(...args) => onExpand(...args)}
                        selectedKeys={this.state.selectedKeys}
                        showIcon={false}
                        showLine
                    >
                        {treeNodes}
                    </Tree>
                </div>
            )
        }

        const getEventSource = e => (e.clientX !== undefined ? e : e.touches[0])

        const onMouseUp = (e) => {
            this.dragState = ({ ...this.dragState, dragging: false })
        }

        const onMouseDown = (e) => {
            const { clientY } = getEventSource(e)
            this.dragState = ({ ...this.dragState, dragging: true, lastClientY: clientY })
        }

        const calcOffsetTop = (diff) => {
            const scrollMax = Math.max(0, this.dragState.container.clientHeight - treeHeight)
            // Top must be scrollMax >= 0
            let offsetTop = -this.dragState.container.offsetTop + diff
            if (offsetTop < 0) offsetTop = 0
            if (offsetTop > scrollMax) offsetTop = scrollMax
            return offsetTop
        }

        const onMouseMove = (e) => {
            const { clientY } = getEventSource(e)
            if (this.dragState.dragging) {
                const diff = this.dragState.lastClientY - clientY
                const offsetTop = calcOffsetTop(diff)
                this.dragState.container.style.top = `${-offsetTop}px`
                this.dragState.lastClientY = clientY
            }
        }

        const onWheel = (e) => {
            const offsetTop = calcOffsetTop(e.deltaY)
            this.dragState.container.style.top = `${-offsetTop}px`
        }

        return (
            <div>
                <div className={theme.toolbuttonPanel}>
                    <button
                        className={theme.toolbutton}
                        onClick={onFilterEmpty}
                        style={{ color: this.state.filterEmpty ? 'darkorange' : 'inherit' }}
                        title="Show only frameworks with data"
                    >
                        <i className={'fa fa-filter'} />
                    </button>
                    <button
                        className={theme.toolbutton}
                        onClick={onReloadFrameworks}
                        title="Reload frameworks"
                    >
                        <i className={'fa fa-refresh'} />
                    </button>
                </div>
                <div>
                    <div style={{ marginTop: '20px', marginLeft: '10px' }}>
                        <label className={theme.label}>
                            Search:
                            <input
                                className={theme.input}
                                type="text"
                                onChange={e => this.setState({ ...this.state, searchText: e.currentTarget.value })}
                                onKeyPress={(e) => { if (e.key === 'Enter') onSearch() }}
                            />
                        </label>
                        <button
                            className={theme.toolbutton}
                            onClick={onSearch}
                        ><i className={'fa fa-search'} /></button>
                    </div>
                </div>
                <div
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onTouchStart={onMouseDown}
                    onTouchEnd={onMouseUp}
                    onTouchMove={onMouseMove}
                    onWheel={onWheel}
                    role={'presentation'}
                    style={{ height: treeHeight, position: 'relative', userSelect: 'none' }}
                >
                    <div
                        ref={c => (this.dragState.container = c)}
                        style={frameworksDivStyle}
                    >
                        {frameworkDiv}
                    </div>
                </div>
            </div>
        )
    }
}

export default withRouter(Frameworks)
